import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { useAuth } from '../../auth/AuthContext';
import { useWhoAmI } from '../../auth/AdminGate';
import {
  SkuSalesReport,
  SkuSalesSection,
  downloadSkuSalesCsv,
  getSkuSalesReport,
} from '../../api';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; report: SkuSalesReport };

const CAD = new Intl.NumberFormat('en-CA', {
  style: 'currency',
  currency: 'CAD',
});

const money = (cents: number) => CAD.format(cents / 100);

// Units can be fractional (Square sells by weight), but whole numbers
// are the common case — don't render "412.0" for 412.
const units = (n: number) =>
  Number.isInteger(n) ? String(n) : n.toFixed(2);

const SkuSalesReportPage = () => {
  const { idToken } = useAuth();
  const isGod = useWhoAmI().roles.includes('god');
  // `undefined` until the first response tells us which years exist —
  // the server defaults to its most recent year, which becomes the
  // picker's initial value.
  const [year, setYear] = useState<number | undefined>(undefined);
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  useEffect(() => {
    // God-only — don't fire the request for a non-god (it would 403).
    if (!idToken || !isGod) return;
    let cancelled = false;
    setState({ kind: 'loading' });
    getSkuSalesReport(idToken, year)
      .then((report) => {
        if (cancelled) return;
        setState({ kind: 'ready', report });
        // Adopt the server's chosen year on the first load so the
        // picker shows what's actually rendered.
        if (year === undefined && report.year !== 0) setYear(report.year);
      })
      .catch((err) => {
        if (!cancelled) {
          setState({
            kind: 'error',
            message:
              (err as Error)?.message || 'failed to load SKU sales report',
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [idToken, year, isGod]);

  const onDownload = async () => {
    if (!idToken || state.kind !== 'ready') return;
    setDownloading(true);
    setDownloadError(null);
    try {
      await downloadSkuSalesCsv(idToken, state.report.year);
    } catch (err) {
      setDownloadError((err as Error)?.message || 'CSV download failed');
    } finally {
      setDownloading(false);
    }
  };

  if (!isGod) {
    return (
      <Alert severity="warning">
        This report is restricted to god-role users.
      </Alert>
    );
  }

  const report = state.kind === 'ready' ? state.report : null;

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 600, color: '#1f2937', mb: 0.5 }}>
        Sales by SKU
      </Typography>
      <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>
        Every product sold in a calendar year, per shop. Sales before tax,
        net of discounts, before refunds, from in-store Square transactions.
        Sales that
        can&apos;t be tied to a catalogued item appear as Vapes or General
        merchandise so every dollar is accounted for — vapes are identified
        by the &quot;.&quot; reconciliation marker or a $40 / $50 unit price.
      </Typography>

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <TextField
          select
          size="small"
          label="Year"
          value={report ? report.year : ''}
          onChange={(e) => setYear(Number(e.target.value))}
          sx={{ minWidth: 140 }}
          disabled={!report || report.available_years.length === 0}
        >
          {(report?.available_years ?? []).map((y) => (
            <MenuItem key={y} value={y}>
              {y}
            </MenuItem>
          ))}
        </TextField>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={onDownload}
          disabled={!report || downloading || report.sections.length === 0}
        >
          {downloading ? 'Preparing…' : 'Download CSV'}
        </Button>
      </Stack>

      {downloadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {downloadError}
        </Alert>
      )}

      {state.kind === 'loading' && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {state.kind === 'error' && <Alert severity="error">{state.message}</Alert>}

      {report && <ReadyView report={report} />}
    </Box>
  );
};

const ReadyView = ({ report }: { report: SkuSalesReport }) => {
  if (report.sections.length === 0) {
    return <Alert severity="info">No sales recorded in {report.year}.</Alert>;
  }
  return (
    <Stack spacing={3}>
      <SelfCheckPanel report={report} />
      {report.sections.map((s) => (
        <SectionTable key={s.label} section={s} />
      ))}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937' }}>
            All shops — {report.year}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {money(report.gross_cents)}
          </Typography>
        </Stack>
      </Paper>
    </Stack>
  );
};

/** Data-health strip. Shown above the tables so a year with poor
 *  catalog coverage, or one whose lines don't add up to its
 *  transaction headers, is obvious before anyone downloads the file. */
const SelfCheckPanel = ({ report }: { report: SkuSalesReport }) => {
  const c = report.self_check;
  // Below this share of dollars carrying a product identity (catalogued
  // items + vapes), the report isn't meaningfully product-level any more
  // and the reader should know before sending it on. Judgement call,
  // not a hard rule.
  const coverageWeak = c.identified_pct < 75;
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
        Data check — {report.year}
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Chip
          size="small"
          color={coverageWeak ? 'warning' : 'success'}
          label={`${c.identified_pct.toFixed(1)}% identified products`}
        />
        <Chip
          size="small"
          label={`${c.vapes_pct.toFixed(1)}% vapes (of which)`}
        />
        <Chip
          size="small"
          label={`${c.general_pct.toFixed(1)}% general merchandise`}
        />
        <Chip
          size="small"
          color={c.tie_out_ok ? 'success' : 'error'}
          label={
            c.tie_out_ok
              ? `Totals tie out to transactions (${Math.abs(c.diff_pct).toFixed(2)}% drift)`
              : `Off by ${money(Math.abs(c.diff_cents))} vs transactions ` +
                `(${Math.abs(c.diff_pct).toFixed(1)}%)`
          }
        />
        {c.discounts_cents > 0 && (
          <Chip
            size="small"
            variant="outlined"
            label={`${money(c.discounts_cents)} discounts`}
          />
        )}
        <Chip
          size="small"
          variant="outlined"
          label={`${c.transaction_count.toLocaleString('en-CA')} transactions`}
        />
      </Stack>
      {coverageWeak && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Under 75% of this year&apos;s sales carry a product identity, so a
          large share of the value sits in the General merchandise line
          rather than against a named product. The report is still complete
          on dollars.
        </Alert>
      )}
      {!c.tie_out_ok && (
        <Alert severity="error" sx={{ mt: 2 }}>
          Line items sum to {money(c.line_gross_cents)} and{' '}
          {money(c.discounts_cents)} of discounts were applied, leaving{' '}
          {money(c.line_gross_cents - c.discounts_cents)} — but the
          transaction records total {money(c.header_subtotal_cents)} before
          tax. Worth understanding before sending this to anyone outside.
        </Alert>
      )}
    </Paper>
  );
};

const SectionTable = ({ section }: { section: SkuSalesSection }) => {
  // Only worth showing the raw Square codes when we couldn't name the
  // shop — for a named shop they're noise.
  const unmapped = section.label.startsWith('Unmapped terminal');
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="baseline"
        sx={{ mb: 1.5 }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937' }}>
            {section.label}
          </Typography>
          {unmapped && (
            <Typography variant="caption" sx={{ color: '#64748b' }}>
              Square location {section.codes.join(', ')} — not mapped to a
              shop.
            </Typography>
          )}
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {money(section.gross_cents)}
        </Typography>
      </Stack>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>SKU</TableCell>
              <TableCell>Product</TableCell>
              <TableCell>Shop brand</TableCell>
              <TableCell>Category</TableCell>
              <TableCell align="right">Units</TableCell>
              <TableCell align="right">Gross</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {section.rows.map((r) => (
              <TableRow key={`${r.tenant}:${r.item_id}`} hover>
                <TableCell sx={{ fontFamily: 'monospace' }}>{r.sku}</TableCell>
                <TableCell>{r.name}</TableCell>
                <TableCell>{r.tenant_label}</TableCell>
                <TableCell>{r.category ?? '—'}</TableCell>
                <TableCell align="right">{units(r.units)}</TableCell>
                <TableCell align="right">{money(r.gross_cents)}</TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell />
              <TableCell sx={{ fontStyle: 'italic' }}>Vapes</TableCell>
              <TableCell colSpan={2} />
              <TableCell align="right">{units(section.vapes.units)}</TableCell>
              <TableCell align="right">
                {money(section.vapes.gross_cents)}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell />
              <TableCell sx={{ fontStyle: 'italic' }}>
                General merchandise (no catalog SKU)
              </TableCell>
              <TableCell colSpan={2} />
              <TableCell align="right">
                {units(section.general.units)}
              </TableCell>
              <TableCell align="right">
                {money(section.general.gross_cents)}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell />
              <TableCell sx={{ fontWeight: 600 }}>
                {section.label} total
              </TableCell>
              <TableCell colSpan={2} />
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                {units(section.units)}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                {money(section.gross_cents)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default SkuSalesReportPage;
